/**
 * Spatial Insights Card
 *
 * Renders a small collapsible HTML card explaining the Spatial Analysis
 * Advisor's recommendation for the current query. Appended to the result
 * panel by overlay.js. Turns every map query into a micro-lesson in
 * cartographic methodology.
 *
 * The card uses a native <details>/<summary> element so it collapses
 * without any JavaScript. All text is escaped — the card never trusts
 * user input, but the knowledge base is also escaped defensively in
 * case a textbook entry contains HTML-like glyphs.
 */

const CLASSIFICATION_LABELS = {
  auto: null,
  none: null,
  jenks: 'Natural breaks (Jenks) — groups similar values, maximizes between-class differences',
  quantile: 'Quantile — equal number of features per class',
  'equal-interval': 'Equal interval — same value range per class',
  'standard-deviation': 'Standard deviation — highlights values above and below the mean',
  'log-quantile': 'Log-quantile — handles highly skewed data like population or GDP',
  custom: 'Custom breaks',
  bivariate: 'Bivariate — two variables encoded simultaneously',
};

const RAMP_LABELS = {
  sequential: 'Sequential — light to dark for low to high values',
  diverging: 'Diverging — two hues from a meaningful center point',
  qualitative: 'Qualitative — distinct colors for categories (no implied order)',
  bivariate: 'Bivariate matrix — 2D color grid encoding two variables',
};

const SOURCE_LABELS = {
  v1: 'Esri Guide to GIS Analysis, Vol. 1',
  v2: 'Esri Guide to GIS Analysis, Vol. 2',
  v3: 'Esri Guide to GIS Analysis, Vol. 3',
  gk: 'Grekousis, Spatial Analysis Methods',
};

const DATA_TYPE_LABELS = {
  point: 'point features',
  line: 'linear features',
  polygon: 'polygon areas',
  continuous_field: 'a continuous surface',
  summarized_area: 'data summarized by area',
};

const ATTRIBUTE_TYPE_LABELS = {
  nominal: 'categorical',
  ordinal: 'ranked',
  interval_ratio: 'ratio-level',
};

const CLASSIFICATION_RATIONALE = {
  'log-quantile': 'The classification uses log-quantile breaks because the data is heavily right-skewed — a few observations have much higher values than most.',
  jenks: 'Natural breaks classification groups similar values together and maximizes the differences between classes.',
  quantile: 'Quantile classification puts an equal number of features in each class, making it easy to compare relative rankings.',
  'equal-interval': 'Equal interval classification divides the data range into equal-width classes — useful when the value range is meaningful.',
  'standard-deviation': 'Standard deviation classification highlights how far values are from the mean — good for spotting unusual highs and lows.',
  bivariate: 'Both variables are classified independently and combined into a 2D color matrix.',
};

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatClassification(cls) {
  if (!cls) return null;
  if (cls in CLASSIFICATION_LABELS) return CLASSIFICATION_LABELS[cls];
  return cls;
}

function formatRampType(type) {
  if (!type) return null;
  return RAMP_LABELS[type] || type;
}

function formatSources(sources) {
  if (!Array.isArray(sources) || sources.length === 0) return '';
  const named = sources.map((s) => SOURCE_LABELS[s] || s).filter(Boolean);
  if (named.length === 0) return '';
  return 'Source: ' + named.join('; ');
}

function formatDataType(dt) {
  return DATA_TYPE_LABELS[dt] || dt || '';
}

function formatAttributeType(at) {
  return ATTRIBUTE_TYPE_LABELS[at] || at || '';
}

function classificationRationale(cls) {
  return CLASSIFICATION_RATIONALE[cls] || '';
}

/**
 * Build a natural-language explanation paragraph from the advisor's
 * enrichment object. Combines the inferred data profile, the method's
 * `when_to_use` text, and a classification rationale when relevant.
 */
function buildExplanation(advice) {
  const parts = [];
  const inferred = advice._inferred || advice.inferred || null;

  if (inferred) {
    const dt = inferred.data_type;
    const at = inferred.attribute_type;
    if (dt && at) {
      parts.push(`Your data is ${formatAttributeType(at)} ${formatDataType(dt)}.`);
    }
  }

  if (advice.when_to_use) {
    parts.push(String(advice.when_to_use));
  }

  const cls = advice.ev_classification;
  if (cls && cls !== 'auto' && cls !== 'none') {
    const rationale = classificationRationale(cls);
    if (rationale) parts.push(rationale);
  }

  return parts.join(' ');
}

/**
 * Build the HTML card. Returns an empty string when the advice is
 * missing, below the confidence threshold, or otherwise unusable — the
 * caller can safely concatenate the return value into the result panel.
 *
 * @param {object} spatialAdvice - enrichment from adviseSpatialMethod()
 * @returns {string} HTML markup (or empty string)
 */
export function buildSpatialInsightsCard(spatialAdvice) {
  if (!spatialAdvice || !spatialAdvice.method) return '';
  const confidence = Number(spatialAdvice.confidence);
  if (!Number.isFinite(confidence) || confidence < 0.8) return '';

  const title = spatialAdvice.label || spatialAdvice.method;
  const explanation = buildExplanation(spatialAdvice);
  const classification = formatClassification(spatialAdvice.ev_classification);
  const rampType = formatRampType(spatialAdvice.color_ramp_type);
  const pitfalls = Array.isArray(spatialAdvice.pitfalls) ? spatialAdvice.pitfalls : [];
  const alternatives = Array.isArray(spatialAdvice.alternatives)
    ? spatialAdvice.alternatives
    : [];
  const sources = formatSources(spatialAdvice.source_texts);

  const firstAlternative =
    alternatives.length > 0
      ? (alternatives[0].label || alternatives[0].id || alternatives[0].method || alternatives[0])
      : null;

  const confidencePct = Math.round(confidence * 100);

  const classificationHtml = classification
    ? `<p class="spatial-insights-detail"><strong>Classification:</strong> ${escapeHtml(classification)}</p>`
    : '';
  const rampHtml = rampType
    ? `<p class="spatial-insights-detail"><strong>Color scheme:</strong> ${escapeHtml(rampType)}</p>`
    : '';
  const pitfallHtml = pitfalls.length > 0
    ? `<p class="spatial-insights-detail"><strong>Watch for:</strong> ${escapeHtml(pitfalls[0])}</p>`
    : '';
  const altHtml = firstAlternative
    ? `<p class="spatial-insights-detail"><strong>Alternative method:</strong> ${escapeHtml(firstAlternative)}</p>`
    : '';
  const sourceHtml = sources
    ? `<p class="spatial-insights-source">${escapeHtml(sources)}</p>`
    : '';

  return [
    '<details class="spatial-insights-card">',
    '  <summary class="spatial-insights-summary">',
    '    <span class="spatial-insights-icon">&#128300;</span>',
    `    <span class="spatial-insights-title">Spatial method: ${escapeHtml(title)}</span>`,
    `    <span class="spatial-insights-confidence">${confidencePct}%</span>`,
    '  </summary>',
    '  <div class="spatial-insights-body">',
    explanation
      ? `    <p class="spatial-insights-explanation">${escapeHtml(explanation)}</p>`
      : '',
    `    ${classificationHtml}`,
    `    ${rampHtml}`,
    `    ${pitfallHtml}`,
    `    ${altHtml}`,
    `    ${sourceHtml}`,
    '  </div>',
    '</details>',
  ]
    .filter((l) => l.trim().length > 0)
    .join('\n');
}

export default { buildSpatialInsightsCard };
